import { useState } from 'react'
import { AdminLayout } from '@/layouts/admin-layout'
import { AppHeader } from '@/components/app-header'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Link, router } from '@inertiajs/react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import { XIcon, AlertTriangleIcon, LightbulbIcon } from 'lucide-react'
import { StarRating } from '@/components/ui/star-rating'

interface Review {
  id: number
  score: number
  feedback: string | null
  improvement_suggestion: string | null
  dev_instance_id: number
  created_at: string
}

interface Gotcha {
  id: number
  name: string
  description: string
  domain: string
  tags: string[]
  keywords: string[]
  message: string
  explanation: string | null
  trigger_tool: string | null
  trigger_file_pattern: string | null
  trigger_content: string[]
  trigger_content_mode: string | null
  check_type: string | null
  check_pattern: string | null
  related_manuals: string[]
  active: boolean
  rejection_reason: string | null
  reviewed_by: string | null
  reviewed_at: string | null
  trigger_count: number
  injection_count: number
  search_count: number
  average_rating: number
  reviews_count: number
  has_suggestions: boolean
  needs_attention: boolean
  recent_reviews: Review[]
  message_id: number | null
  dev_instance_id: number | null
  created_at: string
}

interface Props {
  gotcha: Gotcha
}

function Show({ gotcha }: Props) {
  const [isRejecting, setIsRejecting] = useState(false)
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false)
  const [rejectionReason, setRejectionReason] = useState('')

  const basePath = '/admin/collective_knowledge/gotchas'

  const handleReject = () => {
    setIsRejecting(true)
    router.post(`${basePath}/${gotcha.id}/reject`, {
      reason: rejectionReason
    }, {
      onFinish: () => {
        setIsRejecting(false)
        setRejectDialogOpen(false)
        setRejectionReason('')
      }
    })
  }

  return (
    <>
      <AppHeader breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Gotchas', href: basePath },
        { label: gotcha.name }
      ]} />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6">
              {/* Page Header */}
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-3">
                    <h1 className="text-3xl font-bold text-foreground">{gotcha.name}</h1>
                    <Badge variant={gotcha.active ? 'success' : 'destructive'}>
                      {gotcha.active ? 'Active' : 'Rejected'}
                    </Badge>
                    <Badge variant="secondary">{gotcha.domain}</Badge>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{gotcha.description}</p>
                </div>
                <Button variant="outline" asChild>
                  <Link href={basePath}>Back to list</Link>
                </Button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Warning Message */}
                  <Card className="shadow-none border-amber-300">
                    <CardHeader>
                      <CardTitle>Warning Message</CardTitle>
                      <CardDescription>Shown to Claude when this gotcha fires</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="p-4 bg-amber-50 dark:bg-amber-950/20 rounded-md text-sm">
                        {gotcha.message}
                      </div>
                    </CardContent>
                  </Card>

                  {gotcha.explanation && (
                    <Card className="shadow-none">
                      <CardHeader>
                        <CardTitle>Explanation</CardTitle>
                        <CardDescription>Why this gotcha exists and how to fix it</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <pre className="p-4 bg-muted rounded-md overflow-x-auto text-sm whitespace-pre-wrap">
                          {gotcha.explanation}
                        </pre>
                      </CardContent>
                    </Card>
                  )}

                  {/* Trigger Configuration */}
                  <Card className="shadow-none">
                    <CardHeader>
                      <CardTitle>Trigger Configuration</CardTitle>
                      <CardDescription>Conditions that fire this gotcha during tool use</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <dl className="space-y-4 text-sm">
                        <div className="grid grid-cols-3 gap-2">
                          <dt className="text-muted-foreground font-medium">Tool</dt>
                          <dd className="col-span-2">
                            {gotcha.trigger_tool ? (
                              <Badge variant="secondary">{gotcha.trigger_tool}</Badge>
                            ) : (
                              <span className="text-muted-foreground">Any</span>
                            )}
                          </dd>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <dt className="text-muted-foreground font-medium">File Pattern</dt>
                          <dd className="col-span-2">
                            {gotcha.trigger_file_pattern ? (
                              <code className="px-2 py-1 bg-muted rounded text-xs">{gotcha.trigger_file_pattern}</code>
                            ) : (
                              <span className="text-muted-foreground">Any</span>
                            )}
                          </dd>
                        </div>
                        {gotcha.trigger_content.length > 0 && (
                          <div className="grid grid-cols-3 gap-2">
                            <dt className="text-muted-foreground font-medium">
                              Content Match
                              {gotcha.trigger_content_mode && (
                                <span className="ml-1 text-xs text-muted-foreground font-normal">
                                  ({gotcha.trigger_content_mode})
                                </span>
                              )}
                            </dt>
                            <dd className="col-span-2 flex flex-wrap gap-1">
                              {gotcha.trigger_content.map((tc, i) => (
                                <code key={i} className="px-2 py-1 bg-muted rounded text-xs">{tc}</code>
                              ))}
                            </dd>
                          </div>
                        )}
                        <div className="grid grid-cols-3 gap-2">
                          <dt className="text-muted-foreground font-medium">Check Type</dt>
                          <dd className="col-span-2">
                            {gotcha.check_type ? (
                              <Badge variant="secondary">{gotcha.check_type}</Badge>
                            ) : (
                              <span className="text-muted-foreground">None</span>
                            )}
                          </dd>
                        </div>
                        {gotcha.check_pattern && (
                          <div className="grid grid-cols-3 gap-2">
                            <dt className="text-muted-foreground font-medium">Check Pattern</dt>
                            <dd className="col-span-2">
                              <code className="px-2 py-1 bg-muted rounded text-xs">{gotcha.check_pattern}</code>
                            </dd>
                          </div>
                        )}
                      </dl>
                    </CardContent>
                  </Card>

                  {gotcha.rejection_reason && (
                    <Card className="shadow-none border-destructive">
                      <CardHeader>
                        <CardTitle className="text-destructive">Rejection Reason</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm">{gotcha.rejection_reason}</p>
                        {gotcha.reviewed_by && (
                          <p className="text-xs text-muted-foreground mt-2">
                            By {gotcha.reviewed_by} on {gotcha.reviewed_at ? new Date(gotcha.reviewed_at).toLocaleDateString() : 'N/A'}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                  {/* Actions */}
                  {gotcha.active && (
                    <Card className="shadow-none">
                      <CardHeader>
                        <CardTitle>Actions</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
                          <DialogTrigger asChild>
                            <Button variant="outline" className="w-full justify-start text-red-600">
                              <XIcon className="mr-2 h-4 w-4" />
                              Reject Gotcha
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Reject Gotcha</DialogTitle>
                              <DialogDescription>
                                This will deactivate the gotcha and it will no longer trigger during tool use.
                              </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                              <div className="space-y-2">
                                <Label htmlFor="reason">Reason</Label>
                                <Textarea
                                  id="reason"
                                  value={rejectionReason}
                                  onChange={(e) => setRejectionReason(e.target.value)}
                                  placeholder="Why is this gotcha being rejected?"
                                  rows={3}
                                />
                              </div>
                            </div>
                            <DialogFooter>
                              <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
                                Cancel
                              </Button>
                              <Button
                                variant="destructive"
                                onClick={handleReject}
                                disabled={isRejecting || !rejectionReason.trim()}
                              >
                                {isRejecting ? 'Rejecting...' : 'Reject'}
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </CardContent>
                    </Card>
                  )}

                  {/* Agent Reviews */}
                  <Card className="shadow-none">
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span>Agent Reviews</span>
                        {gotcha.reviews_count > 0 && (
                          <span className="text-sm font-normal text-muted-foreground">({gotcha.reviews_count})</span>
                        )}
                      </CardTitle>
                      {gotcha.reviews_count > 0 && (
                        <div className="flex items-center gap-2">
                          <StarRating value={gotcha.average_rating} size="md" />
                          <span className="text-sm font-medium">{gotcha.average_rating.toFixed(1)}</span>
                        </div>
                      )}
                    </CardHeader>
                    <CardContent>
                      {gotcha.needs_attention && (
                        <div className="mb-3 p-2 bg-amber-50 dark:bg-amber-950/20 rounded-md flex items-start gap-2">
                          <AlertTriangleIcon className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                          <span className="text-xs text-amber-700 dark:text-amber-400">
                            Low rating or pending improvement suggestions. Consider reviewing and updating this gotcha.
                          </span>
                        </div>
                      )}
                      {gotcha.recent_reviews && gotcha.recent_reviews.length > 0 ? (
                        <div className="space-y-3">
                          {gotcha.recent_reviews.map((review) => (
                            <div key={review.id} className="border-b border-border last:border-0 pb-2 last:pb-0">
                              <div className="flex items-center justify-between mb-1">
                                <StarRating value={review.score} />
                                <span className="text-[10px] text-muted-foreground">
                                  {new Date(review.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </span>
                              </div>
                              {review.feedback && (
                                <p className="text-xs text-muted-foreground">{review.feedback}</p>
                              )}
                              {review.improvement_suggestion && (
                                <div className="mt-1 p-1.5 bg-blue-50 dark:bg-blue-950/20 rounded flex items-start gap-1.5">
                                  <LightbulbIcon className="h-3 w-3 text-blue-500 mt-0.5 shrink-0" />
                                  <span className="text-xs text-blue-700 dark:text-blue-400">{review.improvement_suggestion}</span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground">No reviews yet</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Info */}
                  <Card className="shadow-none">
                    <CardHeader>
                      <CardTitle>Info</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <dl className="space-y-3 text-sm">
                        <div>
                          <dt className="text-muted-foreground">Domain</dt>
                          <dd><Badge variant="secondary">{gotcha.domain}</Badge></dd>
                        </div>
                        {gotcha.related_manuals.length > 0 && (
                          <div>
                            <dt className="text-muted-foreground">Related Manuals</dt>
                            <dd className="flex flex-wrap gap-1 mt-1">
                              {gotcha.related_manuals.map((m) => (
                                <Badge key={m} variant="secondary" className="text-xs">{m}</Badge>
                              ))}
                            </dd>
                          </div>
                        )}
                        {gotcha.tags.length > 0 && (
                          <div>
                            <dt className="text-muted-foreground">Tags</dt>
                            <dd className="flex flex-wrap gap-1 mt-1">
                              {gotcha.tags.map((tag) => (
                                <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                              ))}
                            </dd>
                          </div>
                        )}
                        {gotcha.keywords.length > 0 && (
                          <div>
                            <dt className="text-muted-foreground">Keywords</dt>
                            <dd className="flex flex-wrap gap-1 mt-1">
                              {gotcha.keywords.map((kw) => (
                                <Badge key={kw} variant="secondary" className="text-[10px] px-1 py-0">{kw}</Badge>
                              ))}
                            </dd>
                          </div>
                        )}
                        <div>
                          <dt className="text-muted-foreground">Trigger Count</dt>
                          <dd>{gotcha.trigger_count}</dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground">Injection Count</dt>
                          <dd>
                            {gotcha.injection_count}
                            {gotcha.trigger_count > 0 && (
                              <span className="ml-1 text-xs text-muted-foreground">
                                ({Math.round((gotcha.injection_count / gotcha.trigger_count) * 100)}% of triggers)
                              </span>
                            )}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground">Search Count</dt>
                          <dd>{gotcha.search_count}</dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground">Created</dt>
                          <dd>{new Date(gotcha.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</dd>
                        </div>
                        {gotcha.message_id && (
                          <div>
                            <dt className="text-muted-foreground">Source Message</dt>
                            <dd>#{gotcha.message_id}</dd>
                          </div>
                        )}
                        {gotcha.dev_instance_id && (
                          <div>
                            <dt className="text-muted-foreground">Dev Instance</dt>
                            <dd>#{gotcha.dev_instance_id}</dd>
                          </div>
                        )}
                      </dl>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

Show.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Show
